// Marketing · Medición — salud del Pixel/CAPI de Meta, GA4/Google Ads y de las
// cuentas publicitarias de la org, con la lista de problemas a resolver.
// GET /api/v1/admin/marketing/medicion (scope marketing:read).

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, CircleDashed, RefreshCw, XCircle } from 'lucide-react';

import { Button, Card, PageHeader } from '@/modules/orion-shell/ui';

import { demoActiva, fetchMedicion, type Medicion } from '../api';
import { canalLabel, DemoChip, EstadoCarga, num } from '../shared';

type Salud = 'ok' | 'aviso' | 'falta';

function Insignia({ s, children }: { s: Salud; children: string }) {
  const cls = {
    ok: 'bg-primary/10 text-primary',
    aviso: 'bg-destructive/10 text-destructive',
    falta: 'bg-muted text-muted-foreground',
  }[s];
  const Icon = s === 'ok' ? CheckCircle2 : s === 'aviso' ? AlertTriangle : CircleDashed;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}>
      <Icon size={13} aria-hidden /> {children}
    </span>
  );
}

function Renglon({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-all text-right text-foreground">{children}</span>
    </div>
  );
}

function hace(iso: string | null): string {
  if (!iso) return 'nunca';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'hace menos de un minuto';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} días`;
}

const ESTADO_CUENTA: Record<string, { s: Salud; label: string }> = {
  ok: { s: 'ok', label: 'Lista' },
  sin_pago: { s: 'aviso', label: 'Sin método de pago' },
  error: { s: 'aviso', label: 'Con error' },
};

export function MedicionView() {
  const [data, setData] = useState<Medicion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [demo, setDemoState] = useState(demoActiva());

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchMedicion());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
      setDemoState(demoActiva());
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const pixelSalud: Salud = !data?.meta.pixel_id
    ? 'falta'
    : data.meta.ultimo_evento && Date.now() - new Date(data.meta.ultimo_evento).getTime() < 24 * 3600_000
      ? 'ok'
      : 'aviso';
  const eventos = Object.entries(data?.meta.eventos_24h ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="mx-auto w-full max-w-4xl p-4 sm:p-6">
      <PageHeader
        title="Medición"
        subtitle="Si esto falla, las campañas no aprenden y el costo por cliente sube."
        actions={
          <>
            {demo && <DemoChip />}
            <Button variant="ghost" onClick={() => void cargar()} aria-label="Actualizar">
              <RefreshCw size={16} aria-hidden />
            </Button>
          </>
        }
      />

      <EstadoCarga loading={loading && !data} error={error} onRetry={() => void cargar()} onDemo={() => void cargar()}>
        {data && (
          <div className="space-y-4">
            {data.problemas.length > 0 ? (
              <Card title="Por resolver">
                <ul className="space-y-2">
                  {data.problemas.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-foreground">
                      <XCircle size={16} className="mt-0.5 shrink-0 text-destructive" aria-hidden />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : (
              <p className="flex items-center gap-2 rounded-[20px] border border-border bg-card px-4 py-3 text-sm text-foreground">
                <CheckCircle2 size={16} className="text-primary" aria-hidden /> Todo lo que medimos está funcionando.
              </p>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <Card
                title="Meta (Facebook e Instagram)"
                action={<Insignia s={pixelSalud}>{pixelSalud === 'ok' ? 'Recibiendo eventos' : pixelSalud === 'aviso' ? 'Sin eventos recientes' : 'Sin Pixel'}</Insignia>}
              >
                <div className="divide-y divide-border">
                  <Renglon label="Pixel">{data.meta.pixel_id ?? 'No configurado'}</Renglon>
                  <Renglon label="Conversiones desde el servidor (CAPI)">
                    <Insignia s={data.meta.capi ? 'ok' : 'falta'}>{data.meta.capi ? 'Activo' : 'Apagado'}</Insignia>
                  </Renglon>
                  <Renglon label="Último evento">{hace(data.meta.ultimo_evento)}</Renglon>
                </div>
                {eventos.length > 0 && (
                  <>
                    <p className="mt-3 text-xs font-medium text-muted-foreground">Eventos de las últimas 24 h</p>
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {eventos.map(([k, v]) => (
                        <li key={k} className="rounded-full bg-muted px-2.5 py-1 text-xs text-foreground">
                          {k} · <span className="font-semibold tabular-nums">{num(v)}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Card>

              <Card
                title="Google"
                action={<Insignia s={data.google.conectado ? 'ok' : 'falta'}>{data.google.conectado ? 'Conectado' : 'Sin conectar'}</Insignia>}
              >
                <div className="divide-y divide-border">
                  <Renglon label="Google Analytics 4">{data.google.ga4_id ?? 'No configurado'}</Renglon>
                  <Renglon label="Google Ads">{data.google.ads_id ?? 'No configurado'}</Renglon>
                </div>
                {!data.google.conectado && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Conecta Google Ads para medir el gasto y las conversiones de búsqueda en este tablero.
                  </p>
                )}
              </Card>
            </div>

            <Card title="Cuentas publicitarias">
              {data.cuentas.length ? (
                <ul className="divide-y divide-border">
                  {data.cuentas.map((c) => {
                    const e = ESTADO_CUENTA[c.estado] ?? { s: 'aviso' as Salud, label: c.estado };
                    return (
                      <li key={`${c.canal}-${c.id}`} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-foreground">{c.nombre}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {canalLabel(c.canal)} · {c.id} · Paga: {c.paga}
                          </span>
                        </span>
                        <Insignia s={e.s}>{e.label}</Insignia>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Tu organización aún no tiene cuentas publicitarias conectadas.</p>
              )}
            </Card>
          </div>
        )}
      </EstadoCarga>
    </div>
  );
}
